import React, { PureComponent } from 'react';
import { RegInitialConstraint, RegulationOccupancyConstraintDto, RegulationOrMcdmOnlyDto } from 'app/model/CORE';
import MeasureUtils from 'app/common/util/MeasureUtils';
import Utils from 'app/common/util/Utils';
import TimeUtils from 'app/common/util/TimeUtils';
import TitledBorder from 'app/common/panel/TitledBorder';
import { Flex } from 'app/components/common/Flex';
import { Col, InputNumber, Row } from 'antd';
import { CloseCircleOutlined, PlusOutlined } from '@ant-design/icons';
import InputTime from 'app/common/input/InputTime';
import './OccupancyConstraintsEditor.less';
import AppContext from 'app/common/context/AppContext';

interface OccupancyConstraintsEditorProps {
    regulation: RegulationOrMcdmOnlyDto;
    onOccupancyConstraintsUpdate: (update: Partial<RegulationOrMcdmOnlyDto>) => void;
    readonly?: boolean;
}

interface OccupancyConstraintsEditorState {
    regulation: RegulationOrMcdmOnlyDto;
}

export class OccupancyConstraintsEditor extends PureComponent<
    OccupancyConstraintsEditorProps,
    OccupancyConstraintsEditorState
> {
    state = {
        regulation: this.props.regulation,
    };

    componentDidUpdate(prevProps: Readonly<OccupancyConstraintsEditorProps>) {
        if (prevProps?.regulation !== this.props.regulation) {
            this.setState({
                regulation: this.props.regulation,
            });
        }
    }

    updateRegulation = (update: Partial<RegulationOrMcdmOnlyDto>) => {
        let updatedRegulation = Object.assign({}, this.state.regulation, update);
        updatedRegulation = MeasureUtils.adaptConstraintPeriods(
            updatedRegulation,
            false,
            Utils.isNotEmpty(update.occupancyConstraints),
        );
        this.setState({ regulation: updatedRegulation }, () => this.props.onOccupancyConstraintsUpdate(update));
    };

    updateOccupancyConstraint = (idx: number, props: Partial<RegulationOccupancyConstraintDto>) => {
        const updatedConstraint: RegInitialConstraint = Object.assign(
            {},
            this.state.regulation.occupancyConstraints[idx],
            props,
        );
        const newConstraints = this.state.regulation.occupancyConstraints.slice(); // Make a copy of the array (state mgt)
        newConstraints.splice(idx, 1, updatedConstraint);
        this.updateRegulation({ occupancyConstraints: newConstraints });
    };

    updateStartTime = (startTime: Date) => {
        let from = null;

        if (startTime) {
            // Start time date has to be TODAY
            from = TimeUtils.mergeDateAndTime(AppContext.getMonitoredDay(), startTime);
        }
        this.updateRegulation({ applicability: { from, to: this.state.regulation.applicability?.to } });
    };

    removeOccupancyConstraint = (idx: number) => {
        let occupancyConstraints = this.state.regulation.occupancyConstraints?.slice();
        occupancyConstraints?.splice(idx, 1);
        if (!occupancyConstraints?.length) {
            occupancyConstraints = [{ timePeriod: {} }];
        }
        this.updateRegulation({ occupancyConstraints: occupancyConstraints });
    };

    addOccupancyConstraint = () => {
        const occupancyConstraints = this.state.regulation.occupancyConstraints?.slice() || [];
        occupancyConstraints.push({ timePeriod: {} });
        this.updateRegulation({ occupancyConstraints: occupancyConstraints });
    };

    render = () => {
        const regulation = this.state.regulation;
        const occupancyConstraints = regulation.occupancyConstraints;

        return (
            <TitledBorder title="Sub Periods">
                <Flex direction="vertical" size="13em" className="occupancy-constraints overflow header-row">
                    <Row key={`"occupancy-constraint-header"`} gutter={[0, 8]}>
                        <Col span={5}>Start time</Col>
                        <Col span={4}>Peak</Col>
                        <Col span={4}>Rate</Col>
                        <Col span={4}>Pending %</Col>
                        <Col span={7} className={`add-constraint ${this.props.readonly ? 'disabled' : ''}`}>
                            <PlusOutlined onClick={() => this.addOccupancyConstraint()} />
                        </Col>
                    </Row>
                    {occupancyConstraints?.map((occupancyConstraint, idx) => (
                        <Row key={idx} className={`on-hover ${this.props.readonly ? 'disabled' : ''}`} gutter={[0, 8]}>
                            <Col span={5}>
                                <InputTime
                                    value={
                                        idx === 0
                                            ? regulation.applicability?.from
                                            : occupancyConstraint.timePeriod?.from
                                    }
                                    onChange={value => {
                                        if (idx === 0) {
                                            this.updateStartTime(value);
                                        } else {
                                            const from = MeasureUtils.adaptTimeToPeriod(
                                                value,
                                                regulation.applicability,
                                                AppContext.getMonitoredDay(),
                                            );
                                            this.updateOccupancyConstraint(idx, {
                                                timePeriod: {
                                                    from,
                                                    to: occupancyConstraint.timePeriod?.to,
                                                },
                                            });
                                        }
                                    }}
                                />
                            </Col>
                            <Col span={4}>
                                <InputNumber
                                    min={0}
                                    value={occupancyConstraint.peakCapacity}
                                    onChange={(value: number) =>
                                        this.updateOccupancyConstraint(idx, { peakCapacity: value })
                                    }
                                />
                            </Col>
                            <Col span={4}>
                                <InputNumber
                                    min={0}
                                    value={occupancyConstraint.occupancyRate}
                                    onChange={(value: number) =>
                                        this.updateOccupancyConstraint(idx, { occupancyRate: value })
                                    }
                                />
                            </Col>
                            <Col span={4}>
                                <InputNumber
                                    min={0}
                                    value={occupancyConstraint.pendingCapacityPercentage}
                                    onChange={(value: number) =>
                                        this.updateOccupancyConstraint(idx, { pendingCapacityPercentage: value })
                                    }
                                />
                            </Col>
                            <Col className="show-on-hover remove-constraint">
                                <CloseCircleOutlined onClick={() => this.removeOccupancyConstraint(idx)} />
                            </Col>
                        </Row>
                    ))}
                </Flex>
            </TitledBorder>
        );
    };
}
